import {useState, type FormEvent} from 'react'
import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PageHeading} from '@/components/page-heading'
import {DataTable, type Column} from '@/components/data-table'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {FieldError} from '@/components/field-error'
import {Hint} from '@/components/ui/hint'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {useTableState} from '@/hooks/common/use-table-state'
import {useIsMobile} from '@/hooks/common/use-media-query'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {mintUploadTarget, uploadToStorage, useEvidenceUploadFlow} from '@/hooks/sgsst/evidencias/use-evidence'
import {
  PROVEEDORES_PAGE_SIZE,
  useAttachEvaluacionEvidencia,
  useCreateProveedor,
  useCreateProveedorEvaluacion,
  useDeleteProveedor,
  useProveedorEvaluaciones,
  useProveedores,
  useUpdateProveedor,
  type Proveedor,
  type ProveedorEvaluacion,
} from '@/hooks/empresa/proveedores/use-proveedores'
import {translateApiError} from '@/lib/errors'
import {validateNit, validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'
import {SearchInput} from '@/components/ui/search-input'

type ProveedorTipo = Proveedor['tipo']
const TIPOS: ProveedorTipo[] = ['proveedor', 'contratista']

type ProveedorForm = {
  tipo: ProveedorTipo
  nit: string
  razon_social: string
  servicio: string
  contacto: string
  activo: boolean
}

const EMPTY_FORM: ProveedorForm = {
  tipo: 'proveedor',
  nit: '',
  razon_social: '',
  servicio: '',
  contacto: '',
  activo: true,
}

type ProveedorFormErrors = {nit?: string; razon_social?: string; servicio?: string}

function ProveedorFormDialog({
  businessId,
  proveedor,
  open,
  onOpenChange,
}: {
  businessId: string
  proveedor: Proveedor | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const create = useCreateProveedor(businessId)
  const update = useUpdateProveedor(businessId)
  const [form, setForm] = useState<ProveedorForm>(EMPTY_FORM)
  const [fieldErrors, setFieldErrors] = useState<ProveedorFormErrors>({})
  const [error, setError] = useState<string | null>(null)
  const pending = create.isPending || update.isPending

  function handleOpenChange(next: boolean) {
    if (next) {
      setForm(
        proveedor
          ? {
              tipo: proveedor.tipo,
              nit: proveedor.nit,
              razon_social: proveedor.razon_social,
              servicio: proveedor.servicio,
              contacto: proveedor.contacto ?? '',
              activo: proveedor.activo,
            }
          : EMPTY_FORM
      )
      setFieldErrors({})
      setError(null)
    }
    onOpenChange(next)
  }

  function blur(field: keyof ProveedorFormErrors, validate: (raw: string) => string | null) {
    setFieldErrors((prev) => ({...prev, [field]: validate(form[field]) ?? undefined}))
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const problems: ProveedorFormErrors = {
      nit: validateNit(form.nit) ?? undefined,
      razon_social: validateRequired(form.razon_social) ?? undefined,
      servicio: validateRequired(form.servicio) ?? undefined,
    }
    setFieldErrors(problems)
    if (problems.nit || problems.razon_social || problems.servicio) return
    const payload = {
      tipo: form.tipo,
      nit: form.nit.trim(),
      razon_social: form.razon_social.trim(),
      servicio: form.servicio.trim(),
      contacto: form.contacto.trim() || null,
      activo: form.activo,
    }
    const options = {
      onSuccess: () => handleOpenChange(false),
      onError: (err: unknown) => setError(translateApiError(err)),
    }
    if (proveedor) update.mutate({id: proveedor.id, input: payload}, options)
    else create.mutate(payload, options)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="proveedor-form-dialog">
        <DialogHeader>
          <DialogTitle>{t(proveedor ? 'proveedores.edit.title' : 'proveedores.create.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="proveedor-tipo">{t('proveedores.fields.tipo')}</Label>
            <select
              id="proveedor-tipo"
              data-testid="proveedor-tipo"
              value={form.tipo}
              onChange={(e) => setForm({...form, tipo: e.target.value as ProveedorTipo})}
              className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
              {TIPOS.map((tipo) => (
                <option key={tipo} value={tipo}>
                  {t(`proveedores.tipo.${tipo}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="proveedor-nit">{t('proveedores.fields.nit')}</Label>
            <Input
              maxLength={tope('NIT')}
              id="proveedor-nit"
              data-testid="proveedor-nit"
              className="font-mono"
              value={form.nit}
              onChange={(e) => {
                setForm({...form, nit: e.target.value})
                setFieldErrors((prev) => ({...prev, nit: undefined}))
              }}
              onBlur={() => blur('nit', validateNit)}
              aria-invalid={!!fieldErrors.nit}
              required
            />
            <Hint>{t('proveedores.fields.nitHint')}</Hint>
            <FieldError error={fieldErrors.nit} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="proveedor-razon-social">{t('proveedores.fields.razonSocial')}</Label>
            <Input
              maxLength={tope('RAZ')}
              id="proveedor-razon-social"
              data-testid="proveedor-razon-social"
              value={form.razon_social}
              onChange={(e) => {
                setForm({...form, razon_social: e.target.value})
                setFieldErrors((prev) => ({...prev, razon_social: undefined}))
              }}
              onBlur={() => blur('razon_social', validateRequired)}
              aria-invalid={!!fieldErrors.razon_social}
              required
            />
            <FieldError error={fieldErrors.razon_social} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="proveedor-servicio">{t('proveedores.fields.servicio')}</Label>
            <Input
              maxLength={tope('NOTA')}
              id="proveedor-servicio"
              data-testid="proveedor-servicio"
              value={form.servicio}
              onChange={(e) => {
                setForm({...form, servicio: e.target.value})
                setFieldErrors((prev) => ({...prev, servicio: undefined}))
              }}
              onBlur={() => blur('servicio', validateRequired)}
              aria-invalid={!!fieldErrors.servicio}
              required
            />
            <FieldError error={fieldErrors.servicio} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="proveedor-contacto">{t('proveedores.fields.contacto')}</Label>
            <Input
              maxLength={tope('NOM')}
              id="proveedor-contacto"
              data-testid="proveedor-contacto"
              value={form.contacto}
              onChange={(e) => setForm({...form, contacto: e.target.value})}
            />
          </div>
          <FormDialogFooter
            error={error}
            pending={pending}
            submitLabel={t(proveedor ? 'proveedores.edit.submit' : 'proveedores.create.submit')}
            onCancel={() => handleOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

const VERIFICADO_OPTIONS: ProveedorEvaluacion['verificado'][] = ['afiliacion_arl', 'sgsst_propio', 'cumplimiento']
const RESULTADO_OPTIONS: ProveedorEvaluacion['resultado'][] = ['cumple', 'no_cumple']

function EvaluacionFormDialog({
  businessId,
  proveedorId,
  open,
  onOpenChange,
}: {
  businessId: string
  proveedorId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const create = useCreateProveedorEvaluacion(businessId, proveedorId)
  const attach = useAttachEvaluacionEvidencia(businessId, proveedorId)
  const [standardCode, setStandardCode] = useState<'2.9.1' | '2.10.1'>('2.9.1')
  const [verificado, setVerificado] = useState<ProveedorEvaluacion['verificado']>('afiliacion_arl')
  const [fecha, setFecha] = useState('')
  const [fechaError, setFechaError] = useState<string | undefined>(undefined)
  const [resultado, setResultado] = useState<ProveedorEvaluacion['resultado']>('cumple')
  const [proximaFecha, setProximaFecha] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const upload = useEvidenceUploadFlow(async (f, onProgress) => {
    const target = await mintUploadTarget(businessId, f)
    await uploadToStorage(target, f, onProgress)
    const evaluacion = await create.mutateAsync({
      standard_code: standardCode,
      verificado,
      fecha,
      resultado,
      proxima_evaluacion_sugerida: proximaFecha || null,
    })
    await attach.mutateAsync({
      evaluacionId: evaluacion.id,
      payload: {object_key: target.object_key, filename: f.name, content_type: f.type, size_bytes: f.size},
    })
  })

  function handleOpenChange(next: boolean) {
    if (next) {
      setStandardCode('2.9.1')
      setVerificado('afiliacion_arl')
      setFecha('')
      setFechaError(undefined)
      setResultado('cumple')
      setProximaFecha('')
      setFile(null)
      setError(null)
    }
    onOpenChange(next)
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const fechaProblem = validateRequired(fecha)
    setFechaError(fechaProblem ?? undefined)
    if (fechaProblem) return
    if (file) {
      await upload.start({0: file, length: 1, item: () => file} as unknown as FileList)
      if (!upload.error) handleOpenChange(false)
      return
    }
    create.mutate(
      {standard_code: standardCode, verificado, fecha, resultado, proxima_evaluacion_sugerida: proximaFecha || null},
      {onSuccess: () => handleOpenChange(false), onError: (err) => setError(translateApiError(err))}
    )
  }

  const pending = create.isPending || attach.isPending || upload.progress !== null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="evaluacion-form-dialog">
        <DialogHeader>
          <DialogTitle>{t('proveedores.detail.new')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="evaluacion-standard">{t('proveedores.evaluacion.standardCode')}</Label>
            <select
              id="evaluacion-standard"
              data-testid="evaluacion-standard"
              value={standardCode}
              onChange={(e) => setStandardCode(e.target.value as '2.9.1' | '2.10.1')}
              className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
              <option value="2.9.1">2.9.1</option>
              <option value="2.10.1">2.10.1</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="evaluacion-verificado">{t('proveedores.evaluacion.verificado')}</Label>
            <select
              id="evaluacion-verificado"
              data-testid="evaluacion-verificado"
              value={verificado}
              onChange={(e) => setVerificado(e.target.value as ProveedorEvaluacion['verificado'])}
              className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
              {VERIFICADO_OPTIONS.map((v) => (
                <option key={v} value={v}>
                  {t(`proveedores.evaluacion.verificadoOptions.${v}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="evaluacion-fecha">{t('proveedores.evaluacion.fecha')}</Label>
            <Input
              id="evaluacion-fecha"
              data-testid="evaluacion-fecha"
              type="date"
              className="font-mono"
              value={fecha}
              onChange={(e) => {
                setFecha(e.target.value)
                setFechaError(undefined)
              }}
              onBlur={() => setFechaError(validateRequired(fecha) ?? undefined)}
              aria-invalid={!!fechaError}
              required
            />
            <FieldError error={fechaError} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="evaluacion-resultado">{t('proveedores.evaluacion.resultado')}</Label>
            <select
              id="evaluacion-resultado"
              data-testid="evaluacion-resultado"
              value={resultado}
              onChange={(e) => setResultado(e.target.value as ProveedorEvaluacion['resultado'])}
              className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
              {RESULTADO_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {t(`proveedores.evaluacion.resultadoOptions.${r}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="evaluacion-proxima">{t('proveedores.evaluacion.proximaEvaluacionSugerida')}</Label>
            <Input
              id="evaluacion-proxima"
              type="date"
              className="font-mono"
              value={proximaFecha}
              onChange={(e) => setProximaFecha(e.target.value)}
            />
            <Hint>{t('proveedores.evaluacion.proximaEvaluacionHelp')}</Hint>
          </div>
          <div className="space-y-2">
            <Label htmlFor="evaluacion-soporte">{t('proveedores.evaluacion.soporte')}</Label>
            <Input
              id="evaluacion-soporte"
              data-testid="evaluacion-soporte"
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {upload.progress !== null && <p className="text-xs text-muted-foreground">{upload.progress}%</p>}
          </div>
          <FormDialogFooter
            error={error ?? upload.error}
            pending={pending}
            submitLabel={t('proveedores.evaluacion.submit')}
            onCancel={() => handleOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

function EvaluacionesList({
  businessId,
  proveedor,
  canEdit,
}: {
  businessId: string
  proveedor: Proveedor
  canEdit: boolean
}) {
  const {t} = useTranslation()
  const [evalPage, setEvalPage] = useState(1)
  const {data, isLoading} = useProveedorEvaluaciones(businessId, proveedor.id, evalPage)
  const [creating, setCreating] = useState(false)

  return (
    <div className="space-y-3" data-testid="proveedor-evaluaciones">
      <div className="flex items-center justify-end">
        {canEdit && (
          <Button size="sm" variant="outline" data-testid="evaluacion-new" onClick={() => setCreating(true)}>
            {t('proveedores.detail.new')}
          </Button>
        )}
      </div>
      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : !data || data.total === 0 ? (
        <p className="text-sm text-muted-foreground">{t('proveedores.detail.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {data.items.map((ev) => (
            <li
              key={ev.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-border px-4 py-3"
              data-testid={`evaluacion-row-${ev.id}`}>
              <div className="min-w-0">
                <p className="font-mono text-xs text-muted-foreground">
                  {ev.standard_code} · {ev.fecha}
                </p>
                <p className="text-sm text-foreground">
                  {t(`proveedores.evaluacion.verificadoOptions.${ev.verificado}`)}
                </p>
              </div>
              <Tag tone={ev.resultado === 'cumple' ? 'green' : 'red'}>
                {t(`proveedores.evaluacion.resultadoOptions.${ev.resultado}`)}
              </Tag>
            </li>
          ))}
        </ul>
      )}
      {data && data.total > 0 && (
        <PaginationBar page={data.page} pageSize={data.page_size} total={data.total} onPageChange={setEvalPage} />
      )}
      <EvaluacionFormDialog
        businessId={businessId}
        proveedorId={proveedor.id}
        open={creating}
        onOpenChange={setCreating}
      />
    </div>
  )
}

function ProveedorDetailDialog({
  businessId,
  proveedor,
  canEdit,
  onClose,
}: {
  businessId: string
  proveedor: Proveedor | null
  canEdit: boolean
  onClose: () => void
}) {
  const {t} = useTranslation()
  return (
    <Dialog open={!!proveedor} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-xl" data-testid="proveedor-detail-dialog">
        <DialogHeader>
          <DialogTitle>
            {proveedor ? t('proveedores.detail.title', {razonSocial: proveedor.razon_social}) : ''}
          </DialogTitle>
        </DialogHeader>
        {proveedor && <EvaluacionesList businessId={businessId} proveedor={proveedor} canEdit={canEdit} />}
      </DialogContent>
    </Dialog>
  )
}

export function ProveedoresPage() {
  const {t} = useTranslation()
  const {businessId} = useParams<{businessId: string}>()
  const table = useTableState('razon_social')
  const {data, isLoading} = useProveedores(businessId, {
    page: table.page,
    search: table.search,
    sortBy: table.sortBy,
    sortDir: table.sortDir,
  })
  // Con la lista vacía el primario vive dentro del vacío; buscando no cuenta como vacía.
  const listaVacia = !!data && data.items.length === 0 && !table.search
  const deleteProveedor = useDeleteProveedor(businessId ?? '')
  // Cinturón de modo dios, solo de UI: la API conserva sus permisos.
  const canEdit = !useGodModeReadOnly()
  // En el teléfono el NIT y el servicio se desmontan: bajo `table-fixed` apretarían la razón social.
  const enTelefono = useIsMobile()
  const [creating, setCreating] = useState(false)
  const [editTarget, setEditTarget] = useState<Proveedor | null>(null)
  const [detailTarget, setDetailTarget] = useState<Proveedor | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Proveedor | null>(null)

  if (!businessId) return null

  const columns: Column<Proveedor>[] = [
    {
      key: 'razon_social',
      header: t('proveedores.table.razonSocial'),
      cell: (p) => (
        <button
          type="button"
          className="text-left font-medium text-foreground hover:underline"
          onClick={() => setDetailTarget(p)}
          data-testid={`proveedor-open-${p.id}`}>
          {p.razon_social}
        </button>
      ),
    },
    ...(enTelefono
      ? []
      : [
          {
            key: 'nit',
            header: t('proveedores.table.nit'),
            width: 'w-32',
            cell: (p: Proveedor) => <span className="font-mono text-xs">{p.nit}</span>,
          } satisfies Column<Proveedor>,
        ]),
    {
      key: 'tipo',
      header: t('proveedores.table.tipo'),
      width: 'w-28',
      cell: (p) => <CeldaEstado chip={<Tag tone="neutral">{t(`proveedores.tipo.${p.tipo}`)}</Tag>} />,
    },
    ...(enTelefono
      ? []
      : [
          {
            key: 'servicio',
            header: t('proveedores.table.servicio'),
            width: 'w-1/4',
            cell: (p: Proveedor) => <span className="truncate text-xs text-muted-foreground">{p.servicio}</span>,
          } satisfies Column<Proveedor>,
        ]),
    {
      key: 'activo',
      header: t('proveedores.table.activo'),
      width: 'w-28',
      cell: (p) => <CeldaEstado chip={<Tag tone="neutral">{t(`proveedores.activo.${p.activo}`)}</Tag>} />,
    },
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: '',
            width: 'w-36',
            className: 'text-right',
            cell: (p: Proveedor) => (
              <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid={`proveedor-edit-${p.id}`}
                  onClick={() => setEditTarget(p)}>
                  {t('proveedores.actions.edit')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  data-testid={`proveedor-delete-${p.id}`}
                  onClick={() => setDeleteTarget(p)}>
                  {t('proveedores.delete')}
                </Button>
              </div>
            ),
          } satisfies Column<Proveedor>,
        ]
      : []),
  ]

  return (
    <div className="space-y-5" data-testid="proveedores-page">
      {/* Dos «Nuevo proveedor» a la vez romperían el modo estricto de Playwright. */}
      <PageHeading
        title={t('proveedores.title')}
        accion={
          canEdit &&
          !listaVacia && (
            <Button size="sm" data-testid="proveedor-new" onClick={() => setCreating(true)}>
              {t('proveedores.new')}
            </Button>
          )
        }
      />

      <div className="surface overflow-hidden">
        <div className="surface-divider border-b px-4 py-3">
          <SearchInput
            value={table.searchInput}
            onChange={table.setSearchInput}
            placeholder={t('proveedores.searchPlaceholder')}
          />
        </div>

        {isLoading && !data ? (
          <div className="p-4">
            <Skeleton className="h-64 w-full" />
          </div>
        ) : data && data.items.length === 0 ? (
          <div className="px-6 py-14 text-center">
            <p className="font-heading text-lg text-foreground">
              {table.search ? t('proveedores.noResults') : t('proveedores.emptyTitle')}
            </p>
            {!table.search && canEdit && (
              <Button size="sm" className="mt-4" data-testid="proveedor-empty-cta" onClick={() => setCreating(true)}>
                {t('proveedores.new')}
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="px-1">
              <DataTable
                columns={columns}
                rows={data?.items ?? []}
                rowKey={(p) => p.id}
                renderRowDetail={
                  enTelefono
                    ? (p) => (
                        <span className="text-xs text-muted-foreground">
                          <span className="font-mono">{p.nit}</span>
                          {p.servicio ? ` · ${p.servicio}` : ''}
                        </span>
                      )
                    : undefined
                }
              />
            </div>
            {!!data?.total && (
              <div className="surface-divider border-t px-4 py-2.5">
                <PaginationBar
                  page={table.page}
                  pageSize={PROVEEDORES_PAGE_SIZE}
                  total={data.total}
                  onPageChange={table.setPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      <ProveedorFormDialog businessId={businessId} proveedor={null} open={creating} onOpenChange={setCreating} />
      <ProveedorFormDialog
        businessId={businessId}
        proveedor={editTarget}
        open={!!editTarget}
        onOpenChange={(next) => !next && setEditTarget(null)}
      />
      <ProveedorDetailDialog
        businessId={businessId}
        proveedor={detailTarget}
        canEdit={canEdit}
        onClose={() => setDetailTarget(null)}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(next) => !next && setDeleteTarget(null)}
        title={t('proveedores.deleteConfirmTitle', {razonSocial: deleteTarget?.razon_social ?? ''})}
        description={t('proveedores.deleteConfirmBody')}
        confirmLabel={t('proveedores.delete')}
        cancelLabel={t('common.cancel')}
        pending={deleteProveedor.isPending}
        onConfirm={() =>
          deleteTarget &&
          deleteProveedor.mutate(deleteTarget.id, {
            onSuccess: () => setDeleteTarget(null),
          })
        }
      />
    </div>
  )
}
